import { useEffect, useRef } from 'react';
import maplibregl, { type Map as MLMap, type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export interface PunktPracy {
  /** Etykieta na pinezce — numer wiersza w formularzu („1", „2"…). */
  label: string;
  lat: number | null;
  lng: number | null;
}

interface Props {
  points: PunktPracy[];
  /** Wiersz, któremu kliknięcie w mapę ustawia punkt. null = klik nic nie robi. */
  activeIndex: number | null;
  /** Kliknięcie w mapę albo przeciągnięcie pinezki — współrzędne WGS84. */
  onPick: (index: number, lat: number, lng: number) => void;
  /** Klik w pinezkę — wybiera ten wiersz jako aktywny. */
  onSelect?: (index: number) => void;
  /** Punkt klienta — szara pinezka i start widoku, zanim pojawią się punkty prac. */
  clientPoint?: { lat: number; lng: number } | null;
  height?: string;
}

const ACTIVE = '#2563eb';
const IDLE = '#0f766e';
const CLIENT = '#9ca3af';

/** Współrzędne zapisujemy z dokładnością 6 miejsc — tyle trzyma kolumna NUMERIC(9,6). */
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

function pinHtml(fill: string, label: string, big: boolean): string {
  const size = big ? 34 : 28;
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${fill}" stroke="white" stroke-width="1.6">
      <path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/>
    </svg>
    <span style="position:absolute;top:${big ? 6 : 5}px;left:0;right:0;text-align:center;color:white;
      font:700 ${big ? 11 : 10}px/1 system-ui,sans-serif;pointer-events:none">${label}</span>`;
}

/**
 * Mapa do naniesienia prac terenowych przy rejestracji zlecenia: klik stawia punkt aktywnej
 * pracy, pinezkę można przeciągnąć. Celowo osobny, chudy komponent — {@link MapLibreMap} niesie
 * warstwy WMS, PSH i działki, których tu nikt nie potrzebuje, a {@link OverviewMap} nie edytuje.
 */
export function PunktyPracMapa({ points, activeIndex, onPick, onSelect, clientPoint, height = '320px' }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const clientMarkerRef = useRef<maplibregl.Marker | null>(null);
  // Handlery map żyją dłużej niż render — czytają bieżące wartości przez refy.
  const activeRef = useRef(activeIndex);
  const onPickRef = useRef(onPick);
  const onSelectRef = useRef(onSelect);
  const fittedRef = useRef(false);
  useEffect(() => { activeRef.current = activeIndex; }, [activeIndex]);
  useEffect(() => { onPickRef.current = onPick; }, [onPick]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const style: StyleSpecification = {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          maxzoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        },
      },
      layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
    };
    const m = new maplibregl.Map({
      container: containerRef.current,
      style,
      center: [19.1451, 51.9194],
      zoom: 5.5,
    });
    m.addControl(new maplibregl.NavigationControl(), 'top-right');
    m.addControl(new maplibregl.ScaleControl(), 'bottom-left');
    m.getCanvas().style.cursor = 'crosshair';
    m.on('click', e => {
      const i = activeRef.current;
      if (i == null) return;
      onPickRef.current(i, round6(e.lngLat.lat), round6(e.lngLat.lng));
    });
    mapRef.current = m;
    return () => {
      markersRef.current.forEach(mk => mk.remove());
      markersRef.current = [];
      clientMarkerRef.current?.remove();
      clientMarkerRef.current = null;
      m.remove();
      mapRef.current = null;
    };
  }, []);

  // Punkt klienta: tło orientacyjne, nie do przeciągania.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    clientMarkerRef.current?.remove();
    clientMarkerRef.current = null;
    if (!clientPoint) return;
    const el = document.createElement('div');
    el.style.cssText = 'position:relative;opacity:0.85';
    el.innerHTML = pinHtml(CLIENT, '', false);
    el.title = 'Punkt klienta';
    clientMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([clientPoint.lng, clientPoint.lat]).addTo(m);
    // Bez punktów prac start widoku to klient — tam zwykle stoją studnie.
    if (!fittedRef.current && !points.some(p => p.lat != null && p.lng != null)) {
      m.jumpTo({ center: [clientPoint.lng, clientPoint.lat], zoom: 12 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPoint?.lat, clientPoint?.lng]);

  // Pinezki prac — przebudowywane w całości: to kilka punktów, nie warstwa GPS.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    markersRef.current.forEach(mk => mk.remove());
    markersRef.current = [];
    points.forEach((p, i) => {
      if (p.lat == null || p.lng == null) return;
      const active = i === activeIndex;
      const el = document.createElement('div');
      el.style.cssText = 'position:relative;cursor:grab;filter:drop-shadow(0 2px 3px rgba(0,0,0,0.3))';
      el.innerHTML = pinHtml(active ? ACTIVE : IDLE, p.label, active);
      el.addEventListener('click', ev => {
        // Klik w pinezkę wybiera wiersz — nie może przy okazji przestawić punktu aktywnej pracy.
        ev.stopPropagation();
        onSelectRef.current?.(i);
      });
      const mk = new maplibregl.Marker({ element: el, anchor: 'bottom', draggable: true })
        .setLngLat([p.lng, p.lat]).addTo(m);
      mk.on('dragend', () => {
        const ll = mk.getLngLat();
        onPickRef.current(i, round6(ll.lat), round6(ll.lng));
      });
      markersRef.current.push(mk);
    });

    // Dopasowanie widoku tylko raz, przy pierwszych punktach — później użytkownik prowadzi mapę
    // sam i każdy klik nie może odrzucać go na cały zbiór.
    const placed = points.filter(p => p.lat != null && p.lng != null) as { lat: number; lng: number }[];
    if (!fittedRef.current && placed.length > 0) {
      fittedRef.current = true;
      // Punkt postawiony kliknięciem jest już w kadrze — przeskok widoku tylko by dezorientował.
      const view = m.getBounds();
      if (placed.every(p => view.contains([p.lng, p.lat]))) return;
      if (placed.length === 1) {
        m.jumpTo({ center: [placed[0].lng, placed[0].lat], zoom: Math.max(m.getZoom(), 13) });
      } else {
        const b = new maplibregl.LngLatBounds([placed[0].lng, placed[0].lat], [placed[0].lng, placed[0].lat]);
        placed.forEach(p => b.extend([p.lng, p.lat]));
        m.fitBounds(b, { padding: 50, maxZoom: 15, duration: 0 });
      }
    }
  }, [points, activeIndex]);

  return <div ref={containerRef} className="rounded border border-ink-100 overflow-hidden" style={{ width: '100%', height }}/>;
}
